/* «Insertar tabla del hub» en FiboSheets (mejora #4).
 *
 * ── QUÉ ES ─────────────────────────────────────────────────────────────────
 *
 * En vez de copiar a mano la lista de viajes del mes o el padrón de tractores
 * y pegarla en la planilla, un botón la vuelca entera a partir de la celda
 * elegida: encabezados y filas. Y un «Actualizar» la vuelve a traer.
 *
 * ── LA MISMA LISTA CERRADA QUE LAS FÓRMULAS HUB.… ─────────────────────────
 *
 * Nada de elegir tabla y columnas de la base: cada tabla es una pregunta
 * conocida, con sus parámetros validados, sobre una lectura que YA hacen las
 * fórmulas HUB.… (`services/hubDatos`) y con el MISMO permiso que la fórmula
 * hermana (`ver.presidencia` como HUB.VIAJES, `ver.flota` como HUB.VEHICULO…).
 * Por qué una lista y no una puerta a la base está explicado en
 * `shared/planillaHub`: lo mismo vale acá. Se lee con la sesión de quien la
 * inserta, así que la RLS contesta lo que esa persona ya puede ver; el permiso
 * es el cartel —«no tenés acceso»— en vez de una tabla vacía que parece «no
 * hubo viajes». El cartel no nombra la llave (Igna, 06/10: los mensajes no
 * nombran permisos).
 *
 * ── VALORES, NO FÓRMULAS ───────────────────────────────────────────────────
 *
 * Lo que se vuelca son VALORES en las celdas, como si se hubieran tipeado. Así
 * la planilla se exporta, se comparte y la lee el bot igual que cualquier
 * otra: el bot no tiene sesión (sus HUB.… valen #HUB) y quien la recibe
 * compartida ve lo que el dueño volcó, sin tener que poder leerlo él.
 * «Actualizar» es sólo del dueño, y lee con SU sesión y SU permiso.
 *
 * Para poder actualizar, la hoja recuerda cada tabla que se insertó:
 *
 *   hoja.tablasHub = [{ id, fuente, params, desde: "B2", filas, cols,
 *                       actualizada_en, total?, retocada? }]
 *
 * `filas` y `cols` son el tamaño del BLOQUE (con la fila de encabezados).
 * `total` aparece si la fuente tenía más filas que TOPE_FILAS y se recortó.
 * `retocada` la pone insertar filas o columnas ADENTRO del bloque (ver
 * `correrTablasHub` en `planillaOps`, que corre todo esto al insertar/borrar).
 *
 * ── ACTUALIZAR SIN PISAR LO AJENO ──────────────────────────────────────────
 *
 * El bloque viejo es de la tabla: se reescribe y, si la tabla nueva es más
 * corta, lo que sobra se vacía. Lo que la tabla nueva ocupe FUERA del bloque
 * viejo puede ser de la persona (sus totales debajo, una nota al costado):
 * si hay algo ahí, no se escribe nada hasta que elija — correr lo de abajo
 * (se insertan filas, y las fórmulas que miraban ahí se corren con ellas) o
 * pisarlo a conciencia. Lo mismo al insertar sobre celdas con algo.
 *
 * Todo esto es puro (entra una hoja, sale otra) y se prueba sin pantalla; la
 * pantalla es `routes/InsertarTablaHub.jsx`.
 */

import { ErrorHub, choferesDe, buscarDeposito, fechaISO, itemsDelDeposito, unidadDe } from "./planillaHub";
import { cambiarEstructura, escribir } from "./planillaOps";
import { parseRef, refDe } from "./planilla";
import { diaAr, horaAr, hoyAr } from "./fechaAr";
import { ETIQUETA as ETIQUETA_RTO } from "./rto";
import { claveInterno } from "./asignacion";
import { etiquetaTipo as etiquetaTipoAcoplado } from "./tipoAcoplado";
import { estaActiva, textoDeEstado, textoDeEstadoTractor, tractorEstaActivo } from "../services/cols/estado";

/* Más filas que esto no se vuelcan: una planilla se guarda entera (tope de
   TOPE_PLANILLA_MB) y se dibuja en el celular. Se avisa cuántas quedaron afuera. */
export const TOPE_FILAS = 2000;

const mal = (motivo) => new ErrorHub("#VALOR", motivo);
const vacio = (v) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");
const texto = (v) => (vacio(v) ? "" : String(v).trim());
const siNo = (b) => (b ? "Sí" : "No");

/* ── Leer los parámetros ──────────────────────────────────────────────────
 * Llegan del formulario (o de la definición guardada) y se normalizan a lo
 * que se guarda: fechas AAAA-MM-DD, opciones de una lista, números enteros. */

function rango(desde, hasta, topeDias) {
  const a = fechaISO(desde, "«Desde»");
  const b = fechaISO(hasta, "«Hasta»");
  if (a > b) throw mal(`«Desde» (${diaAr(a)}) es posterior a «Hasta» (${diaAr(b)}).`);
  const dias = (Date.parse(b) - Date.parse(a)) / 86400000 + 1;
  if (dias > topeDias) throw mal(`Son ${dias} días; el tope es ${topeDias}. Partilo en tramos.`);
  return { desde: a, hasta: b };
}

function opcion(v, def, etiqueta) {
  const x = texto(v).toLowerCase() || def.defecto;
  if (!def.opciones.some(([k]) => k === x)) throw mal(`${etiqueta}: «${x}» no es una de las opciones.`);
  return x;
}

function entero(v, min, max, etiqueta) {
  const n = Number(vacio(v) ? NaN : String(v).replace(",", "."));
  if (!Number.isInteger(n) || n < min || n > max) throw mal(`${etiqueta} tiene que ser un número entero de ${min} a ${max}.`);
  return n;
}

/* Las definiciones de los parámetros, para el formulario. */
const P_DESDE = { clave: "desde", etiqueta: "Desde", tipo: "fecha", defecto: (ahora) => `${hoyAr(ahora).slice(0, 8)}01` };
const P_HASTA = { clave: "hasta", etiqueta: "Hasta", tipo: "fecha", defecto: (ahora) => hoyAr(ahora) };
const P_FLOTA = { clave: "flota", etiqueta: "Flota", tipo: "opcion", opciones: [["tractores", "Tractores"], ["furgones", "Furgones"]], defecto: "tractores" };
const P_CUALES = { clave: "cuales", etiqueta: "Cuáles", tipo: "opcion", opciones: [["activos", "Sólo activos"], ["todos", "Todos"]], defecto: "activos" };
const P_DIAS = { clave: "dias", etiqueta: "Próximos días", tipo: "numero", min: 1, max: 365, defecto: 30 };
const P_VENCIDAS = { clave: "vencidas", etiqueta: "Ya vencidos", tipo: "opcion", opciones: [["no", "No incluir"], ["si", "Incluir"]], defecto: "no" };

const valorDe = (crudos, p, ahora) => {
  const v = crudos ? crudos[p.clave] : undefined;
  if (!vacio(v)) return v;
  return typeof p.defecto === "function" ? p.defecto(ahora) : p.defecto;
};

/* Un día ISO contra hoy (argentino): cuántos días faltan; negativo si pasó. */
const diasDesdeHoy = (iso, ahora) => Math.round((Date.parse(String(iso).slice(0, 10)) - Date.parse(hoyAr(ahora))) / 86400000);

/* El interno como número cuando es un número (para que =HUB.VEHICULO(A2) o un
   BUSCARV lo encuentren); si no, el texto que haya. */
const internoDe = (...vs) => {
  for (const v of vs) {
    const n = claveInterno(v);
    if (n) return Number(n);
  }
  return texto(vs.find((v) => !vacio(v)));
};
const porInterno = (a, b) => (Number(a[0]) || 0) - (Number(b[0]) || 0);

/* ── Las tablas ───────────────────────────────────────────────────────────
 *
 * Cada una: título y ayuda (lo que muestra el selector), qué permiso pide
 * (alcanza con uno: el mismo que su fórmula HUB.… hermana), sus parámetros,
 * `preparar` (valida y normaliza, tira ErrorHub), `traer` (qué lecturas de
 * `services/hubDatos` usa) y `armar` (puro: de lo traído a encabezados y
 * filas de valores). */

export const TABLAS_HUB = {
  viajes: {
    titulo: "Viajes de un período",
    ayuda: "Un viaje por fila, como el tablero de Presidencia: por fecha de carga (o de salida), sin anulados ni tramos cortos Río Grande ↔ Río Gallegos. Lo mismo que cuenta HUB.VIAJES.",
    permisos: ["ver.presidencia"],
    params: [P_DESDE, P_HASTA, { clave: "sentido", etiqueta: "Sentido", tipo: "opcion", opciones: [["", "Los dos"], ["sur", "Sur"], ["norte", "Norte"]], defecto: "" }],
    preparar(c, ahora) {
      const { desde, hasta } = rango(valorDe(c, P_DESDE, ahora), valorDe(c, P_HASTA, ahora), 400);
      return { desde, hasta, sentido: opcion(c && c.sentido, this.params[2], "Sentido") };
    },
    resumen: (p) => `${diaAr(p.desde)} al ${diaAr(p.hasta)}${p.sentido ? `, ${p.sentido}` : ""}`,
    traer: (d, p) => Promise.all([d.listaViajes(p)]),
    armar(p, [viajes]) {
      return {
        columnas: ["Fecha", "Sentido", "Origen", "Destino", "Cliente", "Tipo", "Acoplado", "Bitrén", "Fletero", "Empresa"],
        filas: (viajes || []).map((v) => [
          diaAr(v.fecha), v.sentido || "", v.origen || "", v.destino || "", v.cliente || "", v.tipo || "",
          vacio(v.tipo_acoplado) ? "" : etiquetaTipoAcoplado(v.tipo_acoplado), siNo(v.bitren), siNo(v.fletero), v.empresa || "",
        ]),
      };
    },
  },

  flota: {
    titulo: "Tractores o furgones",
    ayuda: "El padrón de la flota: interno, patente, tipo, estado, marca y modelo; los tractores, con el chofer asignado. Lo mismo que contesta HUB.VEHICULO.",
    permisos: ["ver.flota"],
    params: [P_FLOTA, P_CUALES],
    preparar(c) {
      return { flota: opcion(c && c.flota, P_FLOTA, "Flota"), cuales: opcion(c && c.cuales, P_CUALES, "Cuáles") };
    },
    resumen: (p) => `${p.flota === "furgones" ? "furgones" : "tractores"}, ${p.cuales === "todos" ? "todos" : "sólo activos"}`,
    traer: (d, p) => (p.flota === "furgones"
      ? Promise.all([d.flota()])
      : Promise.all([d.flota(), d.asignaciones(), d.personas()])),
    armar(p, [flota, asignaciones, personas]) {
      if (p.flota === "furgones") {
        const filas = (flota?.furgones || [])
          .filter((f) => p.cuales === "todos" || estaActiva(f.estado))
          .map((f) => [
            internoDe(f.id, f.interno), f.dominio || "", etiquetaTipoAcoplado(f.tipo_acoplado),
            textoDeEstado(f.estado), f.marca || "", f.modelo || "",
          ]);
        return { columnas: ["Interno", "Patente", "Tipo", "Estado", "Marca", "Modelo"], filas: filas.sort(porInterno) };
      }
      const filas = (flota?.tractores || [])
        .filter((t) => p.cuales === "todos" || tractorEstaActivo(t))
        .map((t) => {
          const n = claveInterno(t.id) || claveInterno(t.interno);
          return [
            internoDe(t.id, t.interno), t.dominio || "", "Tractor", textoDeEstadoTractor(t) || "",
            t.marca || "", t.modelo || "", n ? choferesDe(n, asignaciones, personas) : "",
          ];
        });
      return { columnas: ["Interno", "Patente", "Tipo", "Estado", "Marca", "Modelo", "Chofer"], filas: filas.sort(porInterno) };
    },
  },

  rto: {
    titulo: "Vencimientos de RTO",
    ayuda: "Las unidades a las que se les vence la revisión técnica en los próximos días, como las pantallas de RTO (la fecha del CENT, o la del título si el CENT no tiene nada). Lo mismo que HUB.RTO.",
    permisos: ["ver.documentos"],
    params: [P_FLOTA, P_DIAS, P_VENCIDAS, P_CUALES],
    preparar(c) {
      return {
        flota: opcion(c && c.flota, P_FLOTA, "Flota"),
        dias: entero(valorDe(c, P_DIAS), P_DIAS.min, P_DIAS.max, "«Próximos días»"),
        vencidas: opcion(c && c.vencidas, P_VENCIDAS, "Ya vencidos"),
        cuales: opcion(c && c.cuales, P_CUALES, "Cuáles"),
      };
    },
    resumen: (p) => `${p.flota}, próximos ${p.dias} días${p.vencidas === "si" ? " y vencidos" : ""}`,
    traer: (d, p) => Promise.all([d.rto(p.flota === "furgones" ? "furgon" : "tractor")]),
    armar(p, [filas]) {
      const out = (filas || [])
        // Sin fecha no entra: no es «vence pronto», es «no se sabe», y eso lo
        // dice la pantalla de RTO con su estado.
        .filter((f) => f.vence && Number.isFinite(Number(f.dias)))
        .filter((f) => Number(f.dias) <= p.dias && (p.vencidas === "si" || Number(f.dias) >= 0))
        .filter((f) => p.cuales === "todos" || f.activo !== false)
        .sort((a, b) => Number(a.dias) - Number(b.dias) || (Number(claveInterno(a.interno)) || 0) - (Number(claveInterno(b.interno)) || 0))
        .map((f) => [
          internoDe(f.interno), f.dominio || "", f.marca || "", f.modelo || "", diaAr(f.vence), Number(f.dias),
          ETIQUETA_RTO[f.estado] || f.estado || "",
        ]);
      return { columnas: ["Interno", "Patente", "Marca", "Modelo", "Vence", "Días", "Estado"], filas: out };
    },
  },

  documentos: {
    titulo: "Papeles de choferes por vencer",
    ayuda: "Registro, psicofísico y curso de cargas peligrosas (a quien lo necesita) que vencen en los próximos días, según Documentos. Sólo choferes activos. Lo mismo que HUB.DOC_VENCE.",
    permisos: ["ver.documentos"],
    params: [P_DIAS, P_VENCIDAS],
    preparar(c) {
      return {
        dias: entero(valorDe(c, P_DIAS), P_DIAS.min, P_DIAS.max, "«Próximos días»"),
        vencidas: opcion(c && c.vencidas, P_VENCIDAS, "Ya vencidos"),
      };
    },
    resumen: (p) => `próximos ${p.dias} días${p.vencidas === "si" ? " y vencidos" : ""}`,
    traer: (d) => Promise.all([d.documentos()]),
    armar(p, [filas], ahora) {
      const papeles = [
        ["registro_vence", "Registro", () => true],
        ["psicofisico_vence", "Psicofísico", () => true],
        ["peligrosas_vence", "Cargas peligrosas", (f) => !!f.requiere_peligrosas],
      ];
      const out = [];
      for (const f of filas || []) {
        if (f.activo === false) continue;
        for (const [campo, nombre, aplica] of papeles) {
          if (!f[campo] || !aplica(f)) continue;
          const dias = diasDesdeHoy(f[campo], ahora);
          if (dias > p.dias || (p.vencidas !== "si" && dias < 0)) continue;
          out.push([internoDe(f.dni), f.nombre || "", nombre, diaAr(f[campo]), dias]);
        }
      }
      out.sort((a, b) => a[4] - b[4] || String(a[1]).localeCompare(String(b[1]), "es"));
      return { columnas: ["DNI", "Nombre", "Papel", "Vence", "Días"], filas: out };
    },
  },

  stock: {
    titulo: "Stock del pañol",
    ayuda: "Los artículos del pañol con su cantidad: el total de la pantalla Stock o, con depósito («tractores», «Bahía Blanca»…), lo que hay en ése. Lo mismo que HUB.STOCK.",
    permisos: ["ver.panol"],
    params: [
      { clave: "deposito", etiqueta: "Depósito (opcional)", tipo: "texto", defecto: "", ejemplo: "todos los depósitos" },
      { clave: "solo_con", etiqueta: "Artículos", tipo: "opcion", opciones: [["no", "Todos"], ["si", "Sólo los que hay"]], defecto: "no" },
    ],
    preparar(c) {
      return { deposito: texto(c && c.deposito), solo_con: opcion(c && c.solo_con, this.params[1], "Artículos") };
    },
    resumen: (p) => `${p.deposito ? `depósito ${p.deposito}` : "todos los depósitos"}${p.solo_con === "si" ? ", sólo lo que hay" : ""}`,
    traer: (d, p) => (p.deposito ? Promise.all([d.stock(), d.depositos()]) : Promise.all([d.stock()])),
    armar(p, [stock, depositos]) {
      const num = (v) => (vacio(v) || !Number.isFinite(Number(v)) ? "" : Number(v));
      let filas;
      if (!p.deposito) {
        filas = (stock || []).map((a) => [a.id || a.codigo || "", a.descripcion || "", num(a.cantidad), a.unidad || ""]);
      } else {
        const dep = buscarDeposito(depositos, p.deposito);
        // El mismo artículo puede figurar varias veces (un ingreso y su
        // corrección): lo que hay es la suma, como en HUB.STOCK.
        const suma = new Map();
        for (const it of itemsDelDeposito(dep)) {
          const k = String(it?.id ?? "").toUpperCase();
          if (!k) continue;
          suma.set(k, (suma.get(k) || 0) + (Number(it.cantidad) || 0));
        }
        const catalogo = new Map((stock || []).map((a) => [String(a.id ?? "").toUpperCase(), a]));
        filas = [...suma.entries()].map(([k, n]) => {
          const a = catalogo.get(k) || {};
          return [a.id || k, a.descripcion || "", Math.round(n * 1000) / 1000, a.unidad || ""];
        });
      }
      if (p.solo_con === "si") filas = filas.filter((f) => typeof f[2] === "number" && f[2] > 0);
      filas.sort((a, b) => String(a[0]).localeCompare(String(b[0]), "es"));
      return { columnas: ["Código", "Descripción", "Cantidad", "Unidad"], filas };
    },
  },

  multas: {
    titulo: "Multas impagas",
    ayuda: "Las multas de tránsito guardadas (PBA, CABA y SINAI) que figuran sin pagar, de las patentes que se consultaron en la pantalla Multas. Por DNI no: esa consulta es en vivo y no se guarda.",
    permisos: ["ver.documentos", "admin.permisos"],
    params: [],
    preparar: () => ({}),
    resumen: () => "todas las patentes consultadas",
    traer: (d) => Promise.all([d.multas()]),
    armar(p, [multas]) {
      const filas = (multas?.actas || [])
        .filter((m) => !m.pagada)
        .map((m) => [
          m.dominio || "", m.jurisdiccion || "", m.acta || "", m.ocurrida_en ? diaAr(m.ocurrida_en) : "",
          m.lugar || "", m.descripcion || "",
          vacio(m.monto) || !Number.isFinite(Number(m.monto)) ? "" : Number(m.monto),
          m.ocurrida_en || "",
        ])
        // Por patente y, adentro, la más reciente primero (sin fecha, al final).
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])) || String(b[7]).localeCompare(String(a[7])))
        .map((f) => f.slice(0, 7));
      return { columnas: ["Patente", "Jurisdicción", "Acta", "Fecha", "Lugar", "Descripción", "Monto"], filas };
    },
  },

  urea: {
    titulo: "Cargas de urea",
    ayuda: "Los vales de urea (AdBlue) que registró el bot entre dos fechas, uno por fila. Con interno, sólo los de esa unidad. Lo mismo que suma HUB.UREA. (Las cargas de gasoil no se registran en el hub: no hay de dónde traerlas.)",
    permisos: ["ver.combustible", "ver.urea"],
    params: [P_DESDE, P_HASTA, { clave: "interno", etiqueta: "Interno (opcional)", tipo: "texto", defecto: "", ejemplo: "todos" }],
    preparar(c, ahora) {
      const { desde, hasta } = rango(valorDe(c, P_DESDE, ahora), valorDe(c, P_HASTA, ahora), 400);
      let interno = "";
      if (!vacio(c && c.interno)) {
        const u = unidadDe(c.interno);
        if (!u) throw mal(`«${texto(c.interno)}» no es un interno.`);
        interno = u.clave;
      }
      return { desde, hasta, interno };
    },
    resumen: (p) => `${diaAr(p.desde)} al ${diaAr(p.hasta)}${p.interno ? `, interno ${p.interno}` : ""}`,
    traer: (d, p) => Promise.all([d.urea({ desde: p.desde, hasta: p.hasta })]),
    armar(p, [cargas]) {
      const filas = (cargas || [])
        .filter((c) => !p.interno || (unidadDe(c.interno) || {}).clave === p.interno)
        .map((c) => [
          diaAr(c.fecha), horaAr(c.fecha), internoDe(c.interno), c.patente || "",
          vacio(c.litros) || !Number.isFinite(Number(c.litros)) ? "" : Number(c.litros), c.vale || "",
        ]);
      return { columnas: ["Fecha", "Hora", "Interno", "Patente", "Litros", "Vale"], filas };
    },
  },
};

/* ── Permisos ───────────────────────────────────────────────────────────── */

/* Qué tablas puede insertar alguien con estos permisos. Las que no, igual se
   listan, deshabilitadas: el selector dice que existen y que no le tocan.
   `falta` queda para quien lo necesite en código; la pantalla no lo muestra. */
export function tablasDisponibles(misPermisos) {
  const mios = Array.isArray(misPermisos) ? misPermisos : [];
  return Object.entries(TABLAS_HUB).map(([fuente, t]) => {
    const permitida = t.permisos.some((x) => mios.includes(x));
    return { fuente, titulo: t.titulo, ayuda: t.ayuda, params: t.params, permitida, falta: permitida ? null : t.permisos.join(" o ") };
  });
}

/* Los parámetros con los que arranca el formulario. */
export function paramsIniciales(fuente, ahora = new Date()) {
  const t = TABLAS_HUB[fuente];
  if (!t) return {};
  const out = {};
  for (const p of t.params) out[p.clave] = typeof p.defecto === "function" ? p.defecto(ahora) : p.defecto;
  return out;
}

/* Cómo se nombra una tabla insertada: «Viajes de un período · 01/10/2026 al 31/10/2026». */
export function nombreDeTabla(def) {
  const t = TABLAS_HUB[def && def.fuente];
  if (!t) return "Tabla del hub";
  let r = "";
  try { r = t.resumen(def.params || {}); } catch { r = ""; }
  return r ? `${t.titulo} · ${r}` : t.titulo;
}

/* ── Leer una tabla ─────────────────────────────────────────────────────────
 *
 * Con la capa de datos (`services/hubDatos`, o una de mentira en los tests).
 * El permiso primero: sin él no se sale a buscar nada. Un error se TIRA: una
 * tabla vacía con la base caída se leería «no hay nada». */
export async function leerTabla(fuente, crudos, { datos, ahora = new Date() } = {}) {
  const t = TABLAS_HUB[fuente];
  if (!t) throw new ErrorHub("#NOMBRE", `No hay una tabla del hub «${fuente}».`);
  const params = t.preparar(crudos || {}, ahora);
  const mios = await datos.permisos();
  if (!t.permisos.some((x) => (mios || []).includes(x))) {
    throw new ErrorHub("#HUB", `No tenés acceso a «${t.titulo}».`);
  }
  const partes = await t.traer(datos, params);
  const { columnas, filas } = t.armar(params, partes, ahora);
  const total = filas.length;
  return { fuente, params, columnas, filas: filas.slice(0, TOPE_FILAS), total, recortada: total > TOPE_FILAS };
}

/* ── De valores a celdas ────────────────────────────────────────────────────
 *
 * Una celda guarda TEXTO y el motor lo lee como lo leería si se tipeara.
 * Así que cada valor se escribe de forma que vuelva a ser lo mismo:
 *   · un número, con coma decimal y sin puntos: «1234,5» es 1234,5, mientras
 *     que «1.234» sería mil doscientos treinta y cuatro;
 *   · una fecha, DD/MM/AAAA (ya viene así), que el motor reconoce como fecha;
 *   · un texto que empiece con «=» lleva adelante un espacio de ancho cero,
 *     para que un dato no se vuelva una fórmula. */
export function aCrudo(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return siNo(v);
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return "";
    const n = Math.round(v * 1e6) / 1e6;
    return String(Object.is(n, -0) ? 0 : n).replace(".", ",");
  }
  const s = String(v).replace(/[\r\n\t]+/g, " ");
  return s.trim().startsWith("=") ? `​${s}` : s;
}

/* El bloque que se escribe: encabezados y filas, ya como textos de celda. */
export const bloqueDe = (res) => [res.columnas.map(aCrudo), ...res.filas.map((f) => f.map(aCrudo))];

/* Lo que pesa, aproximado, en el guardado: la planilla entera tiene TOPE_PLANILLA_MB. */
export const pesoDe = (bloque) => JSON.stringify(bloque).length;

/* ── Ubicación y choques ───────────────────────────────────────────────── */

export function areaDe(t) {
  const p = parseRef(t && t.desde);
  if (!p) return null;
  return { r0: p.fila, c0: p.col, r1: p.fila + t.filas - 1, c1: p.col + t.cols - 1 };
}
const areaDeBloque = (r, c, bloque) => ({ r0: r, c0: c, r1: r + bloque.length - 1, c1: c + (bloque[0] ? bloque[0].length : 1) - 1 });
const solapan = (a, b) => !!a && !!b && a.r0 <= b.r1 && b.r0 <= a.r1 && a.c0 <= b.c1 && b.c0 <= a.c1;
const adentro = (a, r, c) => !!a && r >= a.r0 && r <= a.r1 && c >= a.c0 && c <= a.c1;
export const textoDeArea = (a) => (a.r0 === a.r1 && a.c0 === a.c1 ? refDe(a.r0, a.c0) : `${refDe(a.r0, a.c0)}:${refDe(a.r1, a.c1)}`);

/* Las celdas con algo adentro de `area`, salvo las de `salvo`. */
function ocupadas(hoja, area, salvo = null) {
  const out = [];
  const r1 = Math.min(area.r1, hoja.celdas.length - 1);
  for (let r = area.r0; r <= r1; r += 1) {
    const fila = hoja.celdas[r] || [];
    const c1 = Math.min(area.c1, fila.length - 1);
    for (let c = area.c0; c <= c1; c += 1) {
      if (fila[c] !== "" && fila[c] != null && !adentro(salvo, r, c)) out.push({ r, c });
    }
  }
  return out;
}

export const tablasDe = (hoja) => (hoja && Array.isArray(hoja.tablasHub) ? hoja.tablasHub : []);

/* La tabla insertada que contiene una celda (para el «Actualizar» de la
   selección). */
export const tablaEn = (hoja, r, c) => tablasDe(hoja).find((t) => adentro(areaDe(t), r, c)) || null;

/* Antes de insertar: qué se pisaría. */
export function planearInsercion(hoja, celda, bloque) {
  const area = areaDeBloque(celda.r, celda.c, bloque);
  return {
    area,
    colisiones: ocupadas(hoja, area).map(({ r, c }) => refDe(r, c)),
    tablas: tablasDe(hoja).filter((t) => solapan(areaDe(t), area)),
  };
}

/* Escribe el bloque: la fila de encabezados en negrita (sumada al formato
   que ya tuviera la celda). */
function cambiosDelBloque(hoja, r0, c0, bloque) {
  const cambios = [];
  bloque.forEach((fila, i) => fila.forEach((v, j) => {
    const k = { r: r0 + i, c: c0 + j, v };
    if (i === 0) k.fmt = { ...((hoja.formato || {})[refDe(r0, c0 + j)] || {}), bold: true };
    cambios.push(k);
  }));
  return cambios;
}

let secuencia = 0;
const nuevoId = (ahora) => `t${ahora.getTime().toString(36)}${(secuencia += 1).toString(36)}`;

/* Inserta una tabla leída (`res`, de `leerTabla`) con su esquina en `celda`.
   Escribe aunque haya algo: confirmar es de la pantalla. Las tablas que el
   bloque nuevo pisa se olvidan (sus valores ya no están enteros). */
export function insertarTabla(hoja, celda, res, { ahora = new Date(), id = null } = {}) {
  const bloque = bloqueDe(res);
  const area = areaDeBloque(celda.r, celda.c, bloque);
  const quedan = tablasDe(hoja).filter((t) => !solapan(areaDe(t), area));
  const def = {
    id: id || nuevoId(ahora),
    fuente: res.fuente,
    params: res.params,
    desde: refDe(celda.r, celda.c),
    filas: bloque.length,
    cols: bloque[0].length,
    actualizada_en: ahora.toISOString(),
  };
  if (res.recortada) def.total = res.total;
  const n = escribir(hoja, cambiosDelBloque(hoja, celda.r, celda.c, bloque));
  return { ...n, tablasHub: [...quedan, def] };
}

/* Antes de actualizar: dónde cae la tabla nueva y qué hay ahí que no sea
   suyo. `sePuedeCorrer` dice si todo lo que molesta está DEBAJO del bloque
   viejo, y entonces se arregla insertando `filasDeMas` filas. */
export function planearActualizacion(hoja, id, res) {
  const t = tablasDe(hoja).find((x) => x.id === id);
  const vieja = areaDe(t);
  if (!t || !vieja) return null;
  const bloque = bloqueDe(res);
  const nueva = areaDeBloque(vieja.r0, vieja.c0, bloque);
  const choques = ocupadas(hoja, nueva, vieja);
  const otras = tablasDe(hoja).filter((x) => x.id !== id && solapan(areaDe(x), nueva));
  const filasDeMas = Math.max(0, nueva.r1 - vieja.r1);
  const sePuedeCorrer = filasDeMas > 0 && choques.length > 0 && choques.every(({ r }) => r > vieja.r1);
  return {
    tabla: t, vieja, nueva, filasDeMas, sePuedeCorrer,
    colisiones: choques.map(({ r, c }) => refDe(r, c)),
    otras,
    retocada: !!t.retocada,
  };
}

/* Reescribe una tabla con lo recién leído. Lo que el bloque viejo tenía y el
   nuevo no ocupa se vacía; lo de afuera del bloque viejo se escribe tal cual
   (confirmar o correr es de quien llama). */
export function actualizarTabla(hoja, id, res, { ahora = new Date() } = {}) {
  const t = tablasDe(hoja).find((x) => x.id === id);
  const vieja = areaDe(t);
  if (!t || !vieja) return hoja;
  const bloque = bloqueDe(res);
  const nueva = areaDeBloque(vieja.r0, vieja.c0, bloque);
  const cambios = [];
  for (let r = vieja.r0; r <= Math.min(vieja.r1, hoja.celdas.length - 1); r += 1) {
    for (let c = vieja.c0; c <= vieja.c1; c += 1) {
      if (!adentro(nueva, r, c) && hoja.celdas[r] && hoja.celdas[r][c]) cambios.push({ r, c, v: "" });
    }
  }
  cambios.push(...cambiosDelBloque(hoja, vieja.r0, vieja.c0, bloque));
  const n = escribir(hoja, cambios);
  const def = { ...t, params: res.params, filas: bloque.length, cols: bloque[0].length, actualizada_en: ahora.toISOString() };
  delete def.retocada;
  delete def.total;
  if (res.recortada) def.total = res.total;
  const tablasHub = tablasDe(hoja)
    .filter((x) => x.id === id || !solapan(areaDe(x), nueva))
    .map((x) => (x.id === id ? def : x));
  return { ...n, tablasHub };
}

/* Lo mismo sobre el LIBRO, con la opción de correr lo de abajo: se insertan
   `filasDeMas` filas justo después del bloque viejo (así las fórmulas de
   cualquier hoja que miraban ahí se corren con lo que miraban) y recién
   después se escribe. */
export function actualizarEnLibro(hojas, indice, id, res, { correr = false, ahora = new Date() } = {}) {
  let hs = hojas;
  if (correr) {
    const plan = planearActualizacion(hs[indice], id, res);
    if (plan && plan.filasDeMas > 0) hs = cambiarEstructura(hs, indice, "fila", plan.vieja.r1 + 1, plan.filasDeMas);
  }
  const h = hs[indice];
  const n = actualizarTabla(h, id, res, { ahora });
  return n === h && hs === hojas ? hojas : hs.map((x, i) => (i === indice ? n : x));
}

/* Soltar una tabla: los valores quedan, se olvida de dónde vinieron (deja de
   poder actualizarse). */
export function soltarTabla(hoja, id) {
  const quedan = tablasDe(hoja).filter((t) => t.id !== id);
  return quedan.length === tablasDe(hoja).length ? hoja : { ...hoja, tablasHub: quedan };
}

/* ── Guardar y leer del libro ───────────────────────────────────────────────
 * Lo guardado viene de la base (o de un JSON que alguien tocó): sólo se
 * aceptan definiciones con una fuente de la lista, una celda válida y un
 * tamaño razonable. Lo demás se descarta sin romper la planilla. */
export function normalizarTablasHub(lista) {
  if (!Array.isArray(lista)) return [];
  const ok = (n, max) => Number.isInteger(n) && n >= 1 && n <= max;
  // «Actualizar» busca por id: tiene que haber uno, y uno solo por tabla.
  const vistos = new Set();
  const idUnico = (id, i) => {
    let x = typeof id === "string" && id ? id : `t${i}`;
    while (vistos.has(x)) x += "x";
    vistos.add(x);
    return x;
  };
  return lista
    .filter((t) => t && typeof t === "object"
      && TABLAS_HUB[t.fuente]
      && parseRef(t.desde)
      && ok(t.filas, 100000)
      && ok(t.cols, 200))
    .map((t, i) => {
      const n = {
        id: idUnico(t.id, i),
        fuente: t.fuente,
        params: t.params && typeof t.params === "object" && !Array.isArray(t.params) ? t.params : {},
        desde: String(t.desde).toUpperCase(),
        filas: t.filas,
        cols: t.cols,
        actualizada_en: typeof t.actualizada_en === "string" ? t.actualizada_en : null,
      };
      if (Number.isInteger(t.total)) n.total = t.total;
      if (t.retocada) n.retocada = true;
      return n;
    });
}
